import {intersects} from 'ol/extent';
import {useAppSelector} from './redux.ts';
import {useGetStacCollection} from '@src/api/catalog';

/**
 * Determines whether a STAC collection's spatial extent intersects with the current map view extent.
 *
 * @param collectionId - The identifier of the STAC collection to check
 * @returns `true` if the collection extent intersects with the current map extent,
 *          `false` if they do not intersect, or `null` if the check cannot be performed
 *          (e.g., collection is loading, map extent is unavailable, or collection has no spatial extent)
 *
 */
export const useCollectionExtentInView = (
  collectionId: string | null,
): boolean | null => {
  const {data, isLoading} = useGetStacCollection(collectionId);
  const currentMapExtent = useAppSelector(state => state.map.extent);

  if (!collectionId || isLoading || !currentMapExtent) {
    return null;
  }

  const collectionExtent = data?.extent.spatial.bbox[0];
  const doesIntersect =
    collectionExtent?.length === 4 &&
    currentMapExtent?.length === 4 &&
    intersects(collectionExtent, currentMapExtent);
  return doesIntersect;
};
